export type NavMegaMenuIcon =
  | "user"
  | "badge-check"
  | "group"
  | "page"
  | "search"
  | "calendar"
  | "edit-pencil";

export type NavMegaMenuPreviewKey =
  | "company-about"
  | "company-careers"
  | "company-partners"
  | "resources-blog"
  | "resources-insights"
  | "resources-events"
  | "resources-tools";

export interface NavMegaMenuItem {
  title: string;
  description: string;
  url: string;
  external?: boolean;
  icon: NavMegaMenuIcon;
  previewKey: NavMegaMenuPreviewKey;
}

export interface NavMegaMenuGroup {
  title: string;
  items: NavMegaMenuItem[];
}

export const COMPANY_MEGA_MENU: NavMegaMenuGroup = {
  title: "Company",
  items: [
    {
      title: "About",
      description: "Obsessed with great businesses.",
      url: "/about-us",
      icon: "user",
      previewKey: "company-about",
    },
    {
      title: "Careers",
      description: "Accelerate your career at Wayflyer.",
      url: "/careers",
      icon: "badge-check",
      previewKey: "company-careers",
    },
    {
      title: "Partners",
      description: "Explore partnership opportunities.",
      url: "/partners",
      icon: "group",
      previewKey: "company-partners",
    },
  ],
};

export const RESOURCES_MEGA_MENU: NavMegaMenuGroup = {
  title: "Resources",
  items: [
    {
      title: "Blog",
      description: "Learn more about Wayflyer",
      url: "/blog",
      icon: "page",
      previewKey: "resources-blog",
    },
    {
      title: "Insights",
      description: "Data-driven financing insights",
      url: "/data",
      icon: "search",
      previewKey: "resources-insights",
    },
    {
      title: "Events",
      description: "Meet the Wayflyer team in person.",
      url: "/events",
      icon: "calendar",
      previewKey: "resources-events",
    },
    {
      title: "Tools",
      description: "Free calculators for Black Friday planning.",
      url: "/tools",
      icon: "edit-pencil",
      previewKey: "resources-tools",
    },
  ],
};

/** Locale-specific top-level nav labels for the redesign mega menus. */
const NAV_MEGA_MENU_TITLES = {
  company: ["Company", "Unternehmen", "Empresa", "Bedrijf"],
  resources: ["Resources", "Ressourcen", "Recursos", "Bronnen"],
} as const satisfies Record<
  "company" | "resources",
  readonly [string, string, string, string]
>;

export const NAV_MEGA_MENUS = {
  company: COMPANY_MEGA_MENU,
  resources: RESOURCES_MEGA_MENU,
} as const;

const LOCALIZED_NAV_MEGA_MENU_COPY = {
  de: {
    company: [
      {
        title: "Über uns",
        description: "Begeistert von großartigen Unternehmen.",
      },
      {
        title: "Karriere",
        description: "Bringe deine Karriere bei Wayflyer voran.",
      },
      {
        title: "Partner",
        description: "Entdecke Möglichkeiten für eine Partnerschaft.",
      },
    ],
    resources: [
      { title: "Blog", description: "Erfahre mehr über Wayflyer." },
      {
        title: "Einblicke",
        description: "Datenbasierte Einblicke in die Finanzierung.",
      },
      {
        title: "Events",
        description: "Triff das Wayflyer-Team persönlich.",
      },
      {
        title: "Rechner",
        description: "Kostenlose Rechner für die Planung des Black Friday.",
      },
    ],
  },
  es: {
    company: [
      {
        title: "Sobre nosotros",
        description: "Comprometidos con los grandes negocios.",
      },
      {
        title: "Carreras",
        description: "Impulsa tu carrera en Wayflyer.",
      },
      {
        title: "Socios",
        description: "Explora oportunidades de colaboración.",
      },
    ],
    resources: [
      { title: "Blog", description: "Más información sobre Wayflyer." },
      {
        title: "Información",
        description: "Información basada en datos sobre financiación.",
      },
      {
        title: "Eventos",
        description: "Conoce en persona al equipo de Wayflyer.",
      },
      {
        title: "Herramientas",
        description: "Calculadoras gratuitas para planificar el Black Friday.",
      },
    ],
  },
  nl: {
    company: [
      {
        title: "Over ons",
        description: "Gepassioneerd door geweldige bedrijven.",
      },
      {
        title: "Carrières",
        description: "Versnel je carrière bij Wayflyer.",
      },
      {
        title: "Partners",
        description: "Ontdek mogelijkheden voor een samenwerking.",
      },
    ],
    resources: [
      { title: "Blog", description: "Lees meer over Wayflyer." },
      {
        title: "Inzichten",
        description: "Datagedreven financieringsinzichten.",
      },
      {
        title: "Evenementen",
        description: "Ontmoet het Wayflyer-team persoonlijk.",
      },
      {
        title: "Hulpmiddelen",
        description: "Gratis rekenhulpmiddelen voor Black Friday-planning.",
      },
    ],
  },
} as const satisfies Record<
  "de" | "es" | "nl",
  Record<
    keyof typeof NAV_MEGA_MENUS,
    readonly Pick<NavMegaMenuItem, "title" | "description">[]
  >
>;

/** @deprecated Prefer `resolveNavMegaMenu()` — English titles only. */
export const NAV_MEGA_MENU_BY_TITLE: Record<string, NavMegaMenuGroup> = {
  Company: COMPANY_MEGA_MENU,
  Resources: RESOURCES_MEGA_MENU,
};

export function resolveNavMegaMenu(
  menuTitle: string,
  locale?: string
): NavMegaMenuGroup | undefined {
  for (const key of Object.keys(NAV_MEGA_MENU_TITLES) as Array<
    keyof typeof NAV_MEGA_MENU_TITLES
  >) {
    const titles = NAV_MEGA_MENU_TITLES[key] as readonly string[];
    if (titles.includes(menuTitle)) {
      const menu = NAV_MEGA_MENUS[key];
      const localizedCopy =
        LOCALIZED_NAV_MEGA_MENU_COPY[
          locale as keyof typeof LOCALIZED_NAV_MEGA_MENU_COPY
        ]?.[key];

      if (!localizedCopy) return menu;

      return {
        ...menu,
        items: menu.items.map((item, index) => ({
          ...item,
          ...localizedCopy[index],
        })),
      };
    }
  }

  return NAV_MEGA_MENU_BY_TITLE[menuTitle];
}

/** Static fallbacks for previews without a React implementation. */
export const NAV_PREVIEW_IMAGES: Partial<
  Record<NavMegaMenuPreviewKey, string>
> = {
  "company-about": "/images/nav/company-about.png",
  "company-careers": "/images/nav/company-careers.png",
  "company-partners": "/images/nav/company-partners.png",
  "resources-blog": "/images/nav/resources-blog.png",
  "resources-insights": "/images/nav/resources-insights.png",
  "resources-tools": "/images/nav/resources-tools.png",
};
